import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Modal, Popconfirm, Space, TablePaginationConfig, message } from "antd";
import Table, { ColumnType } from "antd/es/table";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import StringUtils from "../../../../../../util/StringUtils";
import SceneApi from "../../../../../../api/SceneApi";
import DeviceSelection from "../../../../../../components/ui-components/device-selection/DeviceSelection";
import DeviceStatus from "../../../../../../components/ui-components/device-status/DeviceStatus";
import { FilterItem, FilterItemLabel, TableWrapper, TenantDevicesWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./SceneDevices.styled";


interface SceneDevicesProps {
    sceneView: any
}

interface TableParams {
    pagination?: TablePaginationConfig;
}

const SceneDevices: React.FC<SceneDevicesProps> = (props)=> {
    // api
    let sceneApi = new SceneApi();
    let navigate = useNavigate();
    let [messageApi, contextHolder] = message.useMessage();

    // state
    let [sceneDevices, setSceneDevices] = useState<any[]>([]);
    let [search, setSearch] = useState<string>("");
    let [loading, setLoading] = useState<boolean>(false);
    let [openAddDeviceModal, setOpenAddDeviceModal] = useState<boolean>(false);
    let [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    let loadSceneDevices = async ()=> {
        let tenantDevices = (await sceneApi.getSceneDeviceView(props.sceneView.scene.id)).data.data;
        setSceneDevices(tenantDevices);
        
        let newTableParams = {pagination: {...tableParams.pagination}};
        newTableParams.pagination.total = tenantDevices.length;
        setTableParams(newTableParams);
    };

    let deviceSelectionFilter = (deviceView)=> {
        if (sceneDevices.find(e=> e.device.id == deviceView.device.id)) {
            return false;
        }
        return true;
    };

    useEffect(()=> {
        loadSceneDevices();
    }, []);
    
    // event
    let onViewDevice = (deviceId: string) => {
        navigate(`/device-management/device/${deviceId}`);
    };

    let onAddDeviceClick = ()=> {
        setOpenAddDeviceModal(true);
    };

    let onDeleteDeviceClick = async (sceneDeviceId)=> {
        await sceneApi.removeDevices(props.sceneView.scene.id, [sceneDeviceId]);
        loadSceneDevices();
        messageApi.open({
            type: 'success',
            content: '删除成功',
        });
    };

    let onSearchKeyDown = (e: any)=> {
        if (e.key === 'Enter') {
            loadSceneDevices()
        }
    };

    let onTableChange = (pagination)=> {
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        loadSceneDevices();
    };

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '设备状态',
            dataIndex: 'device.status',
            key: 'device.status',
            align: 'center',
            fixed: 'left',
            render: (text, record, index)=> {
                return <span><DeviceStatus status={record.device?.status} /></span>
            }
        },
        {
            title: '设备名称',
            dataIndex: 'device.name',
            key: 'device.name',
            align: 'center',
            fixed: 'left',
            render: (text, record, index)=> {
                return <a onClick={()=> onViewDevice(record.device.id)}>{StringUtils.emptyToSlash(record.device?.name)}</a>
            }
        },
        {
            title: '序列号 SN',
            dataIndex: 'device.sn',
            key: 'device.sn',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.node?.sn}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> onViewDevice(record.device.id)}>设备详情</a>
                    <Popconfirm
                            title="移除"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将仅从此场景移除此设备，不会删除设备相关数据，确认要移除么?</div> }
                            onConfirm={()=> onDeleteDeviceClick(record.device.id)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>移除设备</a>
                    </Popconfirm>
                </Space>
            }
        }
    ];

    return <TenantDevicesWrapper>
        {contextHolder}
        <ToolBarWrapper>
            <ToolBarLeftWrapper>
                <Space size="middle">
                    <Button type="primary" icon={<PlusOutlined />} onClick={onAddDeviceClick}>添加设备</Button>
                </Space>
            </ToolBarLeftWrapper>

            <ToolBarRightWrapper>
                <Space size="middle">
                    <FilterItem>
                        <FilterItemLabel>搜索</FilterItemLabel>
                        <Input
                            placeholder="请输入关键字"
                            suffix={<SearchOutlined />}
                            size="middle"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => onSearchKeyDown(e)}
                        />
                    </FilterItem>
                </Space>
            </ToolBarRightWrapper>

            <Modal open={openAddDeviceModal}
                afterClose={() => setOpenAddDeviceModal(false)}
                footer={null}
                closable={false}
                destroyOnClose={true}
                width={1000}
                height={600}
            >
                <DeviceSelection
                    multiSelect={true}
                    filter={deviceSelectionFilter}
                    fetchDeviceViews={async ()=> {
                        return (await sceneApi.getDeviceViewsForNewMapping()).data.data;
                    }}
                    onCancel={() => setOpenAddDeviceModal(false)}
                    onOk={ async (selectedDeviceViews)=> {
                        setOpenAddDeviceModal(false);
                        await sceneApi.addDevices(props.sceneView.scene.id, selectedDeviceViews.map(e=> e.device.id));
                        await loadSceneDevices()
                    }}
                />
            </Modal>
        </ToolBarWrapper>
        <TableWrapper>
            <Table
                rowKey={(record) => record.device?.id}
                columns={columns}
                dataSource={sceneDevices}
                pagination={tableParams.pagination}
                loading={loading}
                onChange={onTableChange}
            />
        </TableWrapper>

    </TenantDevicesWrapper>
}

export default SceneDevices;